import { QualificationQuestionsAnswers } from 'modules/data/domain';
import { useDispatch, useSelector } from 'modules/react-redux';
import ProfileSetupForm from 'pages/auth/components/ProfileSetupForm/ProfileSetupForm';
import { ProfileFormData } from 'pages/auth/components/ProfileSetupForm/ProfileSetupForm.types';
import { getSSOMethodForAnalytics } from 'pages/auth/helpers';
import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { amplitudeService } from 'services/amplitude';
import { gaService } from 'services/ga';
import { useGetInvitations } from 'shared/data/webApp/v2';
import { saveQualificationQuestionsAnswers } from 'shared/helpers';

import { signUpWithCode } from '../../actions';
import { getLoading, getSsoData } from '../../selectors/moduleSelectors';

/**
 * @deprecated Use Redesigned version
 */
const SignUpConsentPopup: FC = memo((props) => {
    const { ...otherProps } = props;

    const loading = useSelector(getLoading);
    const { data } = useGetInvitations();
    const noInvitations = !data?.items.length;
    const ssoData = useSelector(getSsoData);

    const [firstName, setFirstName] = useState(ssoData.firstName || '');
    const [lastName, setLastName] = useState(ssoData.lastName || '');
    const [phone, setPhone] = useState('');

    const dispatch = useDispatch();

    const submit = useCallback(
        (params: {
            qualificationQuestionsAnswers?: QualificationQuestionsAnswers | null;
            consentMarketingAccepted?: boolean;
        }) => {
            amplitudeService.sendData('signup: create user profile');

            dispatch(
                signUpWithCode({
                    oauthProvider: ssoData.oauthProvider,
                    email: ssoData.email,
                    signUpCode: ssoData.signUpCode,
                    subscribe: Boolean(params.consentMarketingAccepted),
                    app: ssoData.app,
                    firstName: firstName,
                    lastName: lastName,
                    phone: phone,
                    startingPoint: ssoData.startingPoint,
                    redirectUrl: ssoData.redirectUrl,
                    qualificationQuestionsAnswers: params.qualificationQuestionsAnswers ?? undefined,
                })
            );

            saveQualificationQuestionsAnswers(params.qualificationQuestionsAnswers);
        },
        [
            dispatch,
            firstName,
            lastName,
            phone,
            ssoData.app,
            ssoData.email,
            ssoData.oauthProvider,
            ssoData.redirectUrl,
            ssoData.signUpCode,
            ssoData.startingPoint,
        ]
    );

    useEffect(() => {
        amplitudeService.sendData('signup: land on user profile setup screen', {
            'signup type': `${ssoData.oauthProvider} sso`,
        });

        if (noInvitations) {
            gaService.sendEvent('land_on_user_profile', {
                method: getSSOMethodForAnalytics(ssoData.oauthProvider),
            });
        }
    }, [ssoData.oauthProvider, noInvitations]);

    const profileData = React.useMemo(() => ({ firstName, phone, lastName }), [firstName, lastName, phone]);

    const handleProfileFormChange = useCallback(
        (data: ProfileFormData) => {
            if (firstName !== data.firstName) {
                setFirstName(data.firstName);
            }

            if (lastName !== data.lastName) {
                setLastName(data.lastName);
            }

            if (phone !== data.phone) {
                setPhone(data.phone || '');
            }
        },
        [firstName, lastName, phone]
    );

    return (
        <ProfileSetupForm
            {...otherProps}
            email={ssoData.email}
            profileData={profileData}
            showPhoneField={noInvitations}
            isLoading={loading}
            showQuestions={noInvitations}
            showConsent
            onDataChange={handleProfileFormChange}
            onSubmit={submit}
        />
    );
});

export default SignUpConsentPopup;
